import { styled, Tooltip, tooltipClasses } from "@mui/material";
import styles from "./battrystatuses.module.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { DashCardLoader } from "../../../../../components/LoaderC";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";

const BootstrapTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
        fontSize: 13,
    },
}));

const addOrdinalSuffix = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const getStatus = (batt, online) => {
    if (online === 0) return "offline";
    if (batt === null || batt === undefined) return "nolock";
    if (batt < 15) return "critical";
    if (batt < 50) return "low";
    return "good";
}

const timeAgo = (mins) => {
    if (mins >= 1440) return `${Math.floor(mins / 1440)}d ago`;
    if (mins >= 60) return `${Math.floor(mins / 60)}h ago`;
    return `${mins}m ago`;
}

const disabledLabel = (reason, short) => {
    if (reason === "no_lock") return short ? "No Lock" : "No lock added";
    return short ? "No Signal" : "No signal";
}

const BatteryIcon = ({ batt, status }) => {
    const width = batt === null ? 0 : Math.max(2, Math.round((batt / 100) * 15));
    return (<svg className={`${styles.batt}`} width="22" height="12" viewBox="0 0 22 12" fill="none">
        <rect x="0.75" y="0.75" width="18.5" height="10.5" rx="2.5" className={`${styles[`stroke-${status}`]}`} strokeWidth="1.4" />
        <rect x="20" y="4" width="2" height="4" rx="1" className={`${styles[`fill-${status}`]}`} />
        <rect x="2.5" y="2.5" width={width} height="7" rx="1.2" className={`${styles[`fill-${status}`]}`} />
    </svg>);
}

const StatGlyph = ({ type }) => {
    if (type === "all") {
        return (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="1" y="7" width="18" height="10" rx="2" /><path d="M23 10v4" />
        </svg>);
    }
    if (type === "critical") {
        return (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        </svg>);
    }
    if (type === "low") {
        return (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="1" y="7" width="14" height="10" rx="2" /><path d="M19 10v4M3 10v4" />
        </svg>);
    }
    return (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M1 1l22 22" />
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.58 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" />
    </svg>);
}

const STAT_CONFIG = [
    { key: "all", label: "Total devices" },
    { key: "critical", label: "Critical" },
    { key: "low", label: "Low battery" },
    { key: "offline", label: "Offline" },
]

const FLOORSOld = [
    {
        name: "5th Floor",
        rooms: [
            { id: "601", batt: 81, sync: 6 },
            { id: "602", batt: 92, sync: 4 },
            { id: "603", batt: 74, sync: 9 },
            { id: "604", batt: 29, sync: 52 },
            { id: "605", batt: 87, sync: 5 },
            { id: "606", batt: 12, sync: 63 },
            { id: "607", batt: 78, sync: 8 },
            { id: "608", batt: null, reason: "no_lock", sync: 0 },
            { id: "610", batt: 83, sync: 7 },
            { id: "611", batt: 90, sync: 3 },
            { id: "612", batt: 76, sync: 10 },
            { id: "Suite", batt: 95, sync: 2 },
            { id: "Garbage Rm 5th FL", batt: 80, sync: 13 },
            { id: "Roof", batt: 34, sync: 120 },
        ],
    },
    {
        name: "4th Floor",
        rooms: [
            { id: "501", batt: 35, sync: 32 },
            { id: "502", batt: 88, sync: 4 },
            { id: "503", batt: 91, sync: 6 },
            { id: "504", batt: 76, sync: 5 },
            { id: "505", batt: 28, sync: 41 },
            { id: "506", batt: 82, sync: 9 },
            { id: "507", batt: 14, sync: 55 },
            { id: "510", batt: 79, sync: 8 },
            { id: "511", batt: 85, sync: 3 },
            { id: "512", batt: 90, sync: 7 },
            { id: "Suite", batt: 94, sync: 2 },
            { id: "Garbage Rm 4th FL", batt: 83, sync: 11 },
            { id: "Roof", batt: 22, sync: 190 },
        ],
    },
    {
        name: "3rd Floor",
        rooms: [
            { id: "401", batt: null, reason: "no_signal", sync: 4320 },
            { id: "402", batt: 87, sync: 12 },
            { id: "403", batt: 93, sync: 6 },
            { id: "404", batt: 71, sync: 9 },
            { id: "405", batt: 80, sync: 14 },
            { id: "406", batt: 31, sync: 38 },
            { id: "407", batt: 77, sync: 5 },
            { id: "408", batt: 84, sync: 7 },
            { id: "409", batt: 89, sync: 10 },
            { id: "410", batt: 73, sync: 13 },
            { id: "411", batt: 81, sync: 6 },
            { id: "412", batt: 95, sync: 4 },
            { id: "Door Lock 3rd FL", batt: 68, sync: 20 },
            { id: "Garbage Rm 3rd FL", batt: 76, sync: 15 },
        ],
    },
    {
        name: "2nd Floor",
        rooms: [
            { id: "301", batt: 86, sync: 9 },
            { id: "302", batt: 8, sync: 6 },
            { id: "303", batt: 5, sync: 11 },
            { id: "304", batt: 79, sync: 7 },
            { id: "305", batt: 83, sync: 5 },
            { id: "306", batt: 90, sync: 8 },
            { id: "307", batt: 18, sync: 47 },
            { id: "308", batt: 25, sync: 29 },
            { id: "309", batt: 77, sync: 6 },
            { id: "310", batt: 33, sync: 35 },
            { id: "311", batt: 85, sync: 10 },
            { id: "312", batt: 92, sync: 4 },
            { id: "Garbage Rm 2nd FL", batt: 70, sync: 22 },
        ],
    },
    {
        name: "1st Floor",
        rooms: [
            { id: "201", batt: 88, sync: 5 },
            { id: "202", batt: 81, sync: 9 },
            { id: "203", batt: 10, sync: 15 },
            { id: "204", batt: 75, sync: 6 },
            { id: "205", batt: 79, sync: 12 },
            { id: "206", batt: 87, sync: 4 },
            { id: "207", batt: 83, sync: 8 },
            { id: "208", batt: 91, sync: 7 },
            { id: "209", batt: 76, sync: 10 },
            { id: "210", batt: 80, sync: 6 },
            { id: "211", batt: 85, sync: 9 },
            { id: "212", batt: null, reason: "no_lock", sync: 0 },
            { id: "Garbage Rm 1st FL", batt: 66, sync: 18 },
        ],
    },
];

const BattryStatuses = ({ selectedProperty }) => {
    const redirect = useHistory();
    const { getAccessTokenSilently } = useAuth0();
    const [noData, setNoData] = useState(true);
    const [contentLoading, setContentLoading] = useState(true);
    const [lockBatteryStatus, setLockBatteryStatus] = useState([]);
    const [FLOORS, setFLOORS] = useState([]);
    const [lastSyncNew, setLastSyncNew] = useState(null);

    useEffect(() => {
        const fetchData = async (pId) => {
            setContentLoading(true);
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            if (pId > 0) {
                await axios.get(process.env.REACT_APP_USER_API_URL + "v1/dashboard/get-device-battery-status/" + pId, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }).then((response) => {
                    setContentLoading(false);
                    setLockBatteryStatus(response.data.data);
                }).catch((error) => {
                    setContentLoading(false);
                    setLockBatteryStatus([]);
                });
            } else {
                setLockBatteryStatus([]);
                setContentLoading(false);
            }
        }

        if (selectedProperty > 0) {
            fetchData(selectedProperty);
        } else {
            fetchData(0);
        }
    }, [selectedProperty, getAccessTokenSilently]);

    useEffect(() => {
        if (lockBatteryStatus?.length > 0) {
            const { floors, latestSync } = convertBatteryData(lockBatteryStatus);
            setFLOORS(floors);
            setLastSyncNew(latestSync);

            let isNoData = true;
            lockBatteryStatus?.map(i => {
                if (i?.electric_quantity) {
                    isNoData = false;
                }

                return null;
            })
            setNoData(isNoData);
        } else {
            setNoData(true);
        }
    }, [lockBatteryStatus]);

    const convertBatteryData = (data) => {
        const now = new Date();
        let latestSync = null;

        const getOrdinal = (n) => {
            const s = ["th", "st", "nd", "rd"];
            const v = n % 100;
            return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
        };

        const floors = Object.values(
            data.reduce((acc, item) => {
                if (!acc[item.floor]) {
                    acc[item.floor] = {
                        floor: item.floor,
                        name: `${getOrdinal(item.floor)} Floor`,
                        rooms: [],
                    };
                }

                let lastsync = '';
                let lastseen = '';
                let reason = '';
                let batt = item.electric_quantity;

                /*if (item.last_seen) {
                    sync = Math.floor(
                        (now - new Date(item.last_seen.replace(" ", "T"))) / 60000
                    );
                }*/

                if (!item.electric_quantity) {
                    reason = 'no_lock';
                } else {
                    if (item.online === 0) {
                        reason = 'no_signal';
                        batt = null;
                        lastseen = dayjs.utc(item?.last_seen).fromNow();
                    } else {
                        lastsync = dayjs.utc(item?.last_sync).fromNow();

                        if (item?.last_sync) {
                            const syncMoment = dayjs.utc(item.last_sync);
                            if (syncMoment.isValid() && (!latestSync || syncMoment.isAfter(latestSync))) {
                                latestSync = syncMoment;
                            }
                        }
                    }
                }

                acc[item.floor].rooms.push({
                    id: item.apt_no.replace("Unit ", ""), // "Unit 101" -> "101"
                    electric_quantity: item.electric_quantity,
                    batt: batt,
                    sync: lastsync,
                    seen: lastseen,
                    reason: reason,
                    online: item.online,
                    device_id: item.device_id,
                });

                return acc;
            }, {})
        )
            // Optional: sort floors descending (5th -> 1st)
            .sort((a, b) => b.floor - a.floor)
            .map(({ floor, ...rest }) => rest);

        return { floors, latestSync: latestSync ? latestSync.fromNow() : null };
    };



    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [lastSync, setLastSync] = useState("2 minutes ago");
    const [spinning, setSpinning] = useState(false);
    const [tooltip, setTooltip] = useState(null);

    const { totals, criticalList, floorsWithChips, visibleCount } = useMemo(() => {
        const totals = { all: 0, critical: 0, low: 0, offline: 0 };
        const criticalList = [];
        let visibleCount = 0;
        const q = search.trim().toLowerCase();

        const floorsWithChips = FLOORS.map((floor) => {
            let floorVisible = false;

            const rooms = floor.rooms.map((room) => {
                const status = getStatus(room.batt, room.online);

                if (room.electric_quantity) {
                    totals.all += 1;
                }

                if (room.electric_quantity && status === "critical") {
                    totals.critical += 1;
                    criticalList.push(room.id);
                }
                if (room.electric_quantity && status === "low") totals.low += 1;
                if (room.electric_quantity && status === "offline") totals.offline += 1;

                const matchesFilter = filter === "all" || status === filter;
                const matchesSearch = !q || room.id.toLowerCase().includes(q);
                const visible = matchesFilter && matchesSearch;
                if (visible) {
                    visibleCount += 1;
                    floorVisible = true;
                }
                return { ...room, status, visible };
            });
            return { ...floor, rooms, floorVisible };
        });

        return { totals, criticalList, floorsWithChips, visibleCount };
    }, [filter, search, FLOORS]);

    const showTooltip = (e, room) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltip({
            x: rect.left + rect.width / 2,
            y: rect.top,
            room,
        });
    }

    const hideTooltip = () => setTooltip(null);

    const handleRefresh = useCallback(() => {
        setSpinning(true);
        setLastSync("just now");
        setTimeout(() => setSpinning(false), 500);
    }, []);



    return <div className={`${styles.wrap}`}>
        <div className={`${styles.card}`}>

            {contentLoading && (<div className={`${styles.LoaderDash2}`}>
                <DashCardLoader />
            </div>)}

            {!contentLoading && (<>

                {/* Header */}
                <div className={`${styles.header}`}>
                    <div className={`${styles.headerLeft}`}>
                        <h1 className={`${styles.title}`}>Battery Status</h1>
                        {!noData && <div className={`${styles.headerSub}`}>
                            <span className={`${styles.liveDot}`} />
                            Live monitoring · {lastSyncNew && <>Last synced <b>{lastSyncNew}</b></>}
                        </div>}
                    </div>
                    {/*!noData && <button
                        type="button"
                        className={`${styles.refreshBtn} ${spinning ? styles.spinning : ""}`}
                        onClick={handleRefresh}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 11a8.1 8.1 0 0 0-15.5-2M4 13a8.1 8.1 0 0 0 15.5 2" />
                            <path d="M4 4v5h5M20 20v-5h-5" />
                        </svg>
                        Sync now
                    </button>*/}
                </div>

                {noData && (<div className={`${styles.NoDataSec}`}>
                    <div className={`${styles.NoDataIMG}`}><img src="/static/img/no-battery.png" alt="" /></div>
                    <p className={`${styles.NoDataText}`}>Oops!</p>
                    <p className={`${styles.NoDataTextSub}`}>Battery Not Detected.</p>
                </div>)}

                {!noData && <>
                    {/* Stats strip */}
                    <div className={`${styles.stats}`}>
                        {STAT_CONFIG.map((stat) => (<button
                            type="button"
                            key={stat.key}
                            className={`${styles.stat} ${styles[stat.key]} ${filter === stat.key ? styles.active : ""}`}
                            onClick={() => setFilter(stat.key)}
                        >
                            <div className={`${styles.statIcon}`}>
                                <StatGlyph type={stat.key} />
                            </div>
                            <div>
                                <div className={`${styles.statNum}`}>{totals[stat.key]}</div>
                                <div className={`${styles.statLabel}`}>{stat.label}</div>
                            </div>
                        </button>))}
                    </div>

                    {/* Alert banner */}
                    {criticalList.length > 0 && (<div className={`${styles.alertBanner}`}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3">
                            <path d="M12 9v4M12 17h.01" />
                            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
                        </svg>
                        <b>{criticalList.length} device{criticalList.length > 1 ? "s" : ""}</b>&nbsp;need immediate attention — {criticalList.join(", ")}
                    </div>)}

                    {/* Toolbar */}
                    <div className={`${styles.toolbar}`}>
                        <div className={`${styles.search}`}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3">
                                <circle cx="11" cy="11" r="7" />
                                <path d="m21 21-4.35-4.35" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search room or device…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className={`${styles.legend}`}>
                            <div className={`${styles.legendItem}`}><span className={`${styles.legendSwatch} ${styles.swatchGood}`} />Good (50%+)</div>
                            <div className={`${styles.legendItem}`}><span className={`${styles.legendSwatch} ${styles.swatchLow}`} />Low (15–49%)</div>
                            <div className={`${styles.legendItem}`}><span className={`${styles.legendSwatch} ${styles.swatchCritical}`} />Critical (&lt;15%)</div>
                            <div className={`${styles.legendItem}`}><span className={`${styles.legendSwatch} ${styles.swatchOffline}`} />No signal</div>
                            <div className={`${styles.legendItem}`}><span className={`${styles.legendSwatch} ${styles.swatchNoLock}`} />No lock added</div>
                        </div>
                    </div>

                    {/* Floors */}
                    <div className={`${styles.floors}`}>
                        {floorsWithChips.map((floor) => (
                            <div key={floor.name} className={`${styles.floor}`} style={{ display: floor.floorVisible ? "flex" : "none" }}>
                                <div className={`${styles.floorLabel}`}>
                                    <div className={`${styles.floorName}`}>{floor.name}</div>
                                    <div className={`${styles.floorCount}`}>{floor.rooms.length} devices</div>
                                </div>
                                <div className={`${styles.floorGrid}`}>
                                    {floor.rooms.map((room) => (
                                        <div
                                            key={room.id}
                                            className={`${styles.chip} ${styles[room.status]} ${room.reason === "no_lock" ? styles.noLock : ""} ${room.visible ? "" : styles.hidden}`}
                                            onMouseEnter={(e) => showTooltip(e, room)}
                                            onMouseMove={(e) => showTooltip(e, room)}
                                            onMouseLeave={hideTooltip}
                                            onClick={(e) => { if (room?.device_id) redirect.push("/smart-devices/details/" + room?.device_id); }}
                                        >
                                            <BatteryIcon batt={room.batt} status={room.status} />
                                            <div>
                                                <div className={`${styles.room}`}>{room.id}</div>
                                                <div className={`${styles.pct}`}>
                                                    {room.batt === null ? disabledLabel(room.reason, true) : `${room.batt}%`}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Footer */}
                    <div className={`${styles.footer}`}>
                        <span>Showing <b>{visibleCount}</b> devices</span>
                        {/*<span>Auto-refreshes every 5 minutes</span>*/}
                    </div>
                </>}
            </>)}
        </div>

        {/* Tooltip */}
        {tooltip && (<div className={`${styles.tooltip} ${styles.show}`} style={{ left: tooltip.x, top: tooltip.y }}>
            <div className={`${styles.tArrow}`} />
            <div className={`${styles.tName}`}>
                <span className={`${styles.statusDot} ${styles[`dot-${tooltip.room.status}`]}`} />
                {tooltip.room.id}
            </div>
            <div className={`${styles.tRow}`}>
                Battery <b>{tooltip.room.batt === null ? disabledLabel(tooltip.room.reason, false) : `${tooltip.room.batt}%`}</b>
            </div>
            {(tooltip.room.reason === "no_lock" ? (<div className={`${styles.tRow}`}>Status <b>Not installed</b></div>) : (tooltip.room.reason === "no_signal" ? (<div className={`${styles.tRow}`}>Last seen <b>{tooltip.room.seen}</b></div>) : (<div className={`${styles.tRow}`}>Last synced <b>{tooltip.room.sync}</b></div>)))}
        </div>
        )}
    </div>;
}

export default BattryStatuses;